export interface ParsedAddress {
  raw: string;

  // Ej: "Carrera" / "Calle" / "Avenida" / "Diagonal" / "Transversal"
  nomenclaturaBase: string | null;

  // Ej: "55B" (parte de "Cra. 55B")
  numeroUno: string | null;

  // Ej: "78" (parte de "# 78-3" o "# 78 3")
  numeroDos: string | null;

  // Ej: "3"
  numeroTres: string | null;

  // Ej: "Carrera 55B"
  nomenclatura: string | null;

  // Formato tipo legacy: "Carrera 55B # 78 - 3"
  formatted: string | null;
}

export class AddressTextParser {
  private static readonly NOM_MAP: Array<{ re: RegExp; long: string }> = [
    { re: /^(cra|kr|cr)\.?$/i, long: 'Carrera' },
    { re: /^(cl|cll)\.?$/i, long: 'Calle' },
    { re: /^(av|avda)\.?$/i, long: 'Avenida' },
    { re: /^(dg)\.?$/i, long: 'Diagonal' },
    { re: /^(tv|trv|tr)\.?$/i, long: 'Transversal' },
  ];

  static parse(addressText: string): ParsedAddress {
    const raw = (addressText ?? '').trim();
    if (!raw) return this.empty(raw);

    const firstPart = raw.split(',')[0].trim(); // "Cra. 55b #783"
    const normalized = firstPart.replace(/\s+/g, ' ').trim();

    const main = normalized.match(
      /^([A-Za-zÁÉÍÓÚÜÑáéíóúüñ]{1,20}\.?)\s+([\dA-Za-z]+)(?:\s*#\s*([\dA-Za-z\- ]+))?$/i
    );

    if (!main) return this.empty(raw);

    const typeToken = main[1];
    const numeroUno = main[2] ?? null;
    const afterHash = (main[3] ?? '').trim();

    const nomenclaturaBase = this.expandType(typeToken);
    const numeroUnoNorm = numeroUno ? String(numeroUno).toUpperCase() : null;

    const { numeroDos, numeroTres } = this.parseAfterHash(afterHash);

    const nomenclatura = nomenclaturaBase ?? null;

    const formatted =
      nomenclatura && numeroDos && numeroTres
        ? `${nomenclatura} # ${numeroDos} - ${numeroTres}`
        : nomenclatura && afterHash
          ? `${nomenclatura} # ${afterHash}`
          : nomenclatura;

    return {
      raw,
      nomenclaturaBase,
      numeroUno: numeroUnoNorm,
      numeroDos,
      numeroTres,
      nomenclatura,
      formatted: formatted ?? null,
    };
  }

  private static expandType(token: string): string | null {
    const t = token.replace(/\./g, '').trim();
    for (const m of this.NOM_MAP) {
      if (m.re.test(t)) return m.long;
    }
    if (/^(carrera|calle|avenida|diagonal|transversal)$/i.test(t)) {
      return t.charAt(0).toUpperCase() + t.slice(1).toLowerCase();
    }
    return null;
  }
  private static parseAfterHash(afterHash: string): { numeroDos: string | null; numeroTres: string | null } {
    const raw = (afterHash ?? '').replace(/\s+/g, ' ').trim();
    if (!raw) return { numeroDos: null, numeroTres: null };

    const tokens = raw.split(' ').filter(Boolean);
    const numericTokens: string[] = [];
    for (const t of tokens) {
      if (!/\d/.test(t)) break;
      numericTokens.push(t);
      if (numericTokens.length >= 2) break;
    }

    if (numericTokens.length === 0) return { numeroDos: null, numeroTres: null };

    const first = numericTokens[0].toUpperCase();

    const dash = first.match(/^([0-9A-Z]+)-([0-9A-Z]+)$/);
    if (dash) return { numeroDos: dash[1], numeroTres: dash[2] };

    if (/^\d{3,}$/.test(first)) {
      return { numeroDos: first.slice(0, 2), numeroTres: first.slice(2) };
    }

    if (numericTokens.length >= 2) {
      const second = numericTokens[1].toUpperCase();
      return { numeroDos: first, numeroTres: /\d/.test(second) ? second : null };
    }

    return { numeroDos: first, numeroTres: null };
  }
  private static empty(raw: string): ParsedAddress {
    return {
      raw,
      nomenclaturaBase: null,
      numeroUno: null,
      numeroDos: null,
      numeroTres: null,
      nomenclatura: null,
      formatted: null,
    };
  }
}
